import React, { useState, useEffect } from 'react';
import { Bell, X, Clock, AlertTriangle, Calendar, User } from 'lucide-react';
import { AppointmentStatusService, PendingAppointment } from '../../services/AppointmentStatusService';
import { useAuth } from '../../context/AuthContext';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import { formatInTimeZone, isValidTimeZone } from '../../utils/timeZone';

interface NotificationBellProps {
  professionalName?: string;
  userRole?: string;
  userId?: string;
  clientId?: string;
  onResolveAppointment: (appointment: PendingAppointment) => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  professionalName,
  userRole,
  userId,
  clientId,
  onResolveAppointment
}) => {
  const { user: authUser, selectedClient } = useAuth();
  const { profile } = useClinicProfile();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState<PendingAppointment[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const resolvedProfessionalName =
    professionalName?.trim() ||
    authUser?.userAssignedCalendarName?.trim() ||
    authUser?.userDisplayName?.trim() ||
    '';
  const resolvedUserRole = (userRole || selectedClient?.userRole || authUser?.userRole || '').trim();
  const resolvedUserId = userId || authUser?.userId || '';
  const resolvedClientId = clientId || selectedClient?.clientId || authUser?.current_client_id || '';
  const clinicTimeZone =
    profile?.clientId === resolvedClientId && isValidTimeZone(profile.timeZone)
      ? profile.timeZone.trim()
      : null;
  const isAdminView = resolvedUserRole.toLowerCase() === 'admin';

  useEffect(() => {
    loadPendingAppointments();
    // Se for necessário um real-time, pode-se implementar Supabase Realtime Subscriptions aqui
    // Por enquanto, a atualização será manual ou baseada em eventos de outras ações.

    // Exemplo de polling simples se real-time não for urgente:
    // const interval = setInterval(() => {
    //   loadPendingAppointments();
    // }, 60000); // Atualiza a cada 1 minuto
    // return () => clearInterval(interval);
  }, [resolvedClientId, resolvedProfessionalName, resolvedUserId, resolvedUserRole]);

  const loadPendingAppointments = async () => {
    try {
      if (!resolvedClientId) {
        setPendingAppointments([]);
        setPendingCount(0);
        return;
      }

      setLoading(true);
      const pending = await AppointmentStatusService.getPendingAppointments({
        clientId: resolvedClientId,
        professionalName: resolvedProfessionalName,
        userId: resolvedUserId,
        userRole: resolvedUserRole,
      });
      setPendingAppointments(pending);
      setPendingCount(pending.length);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high':
        return 'bg-red-100 border-red-300 text-red-800';
      case 'medium':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low':
        return 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  const getUrgencyIcon = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'medium':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'low':
        return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  const formatTimeAgo = (hours: number): string => {
    if (hours < 24) {
      return `${hours}h atrás`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d atrás`;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative rounded-full border p-2.5 shadow-sm transition-colors ${
          showDropdown
            ? 'border-sky-200 bg-sky-50 text-[#235f82]'
            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-sky-200 hover:bg-sky-50 hover:text-[#235f82]'
        }`}
        aria-label="Notificações"
        aria-expanded={showDropdown}
        aria-haspopup="dialog"
      >
        <Bell className="h-7 w-7" strokeWidth={2} />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Agendamentos Pendentes</h3>
                <p className="text-sm text-gray-600">
                  {pendingCount === 0 ? 'Nenhuma pendência' : `${pendingCount} aguardando confirmação`}
                </p>
              </div>
              <button
                onClick={() => setShowDropdown(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Carregando...</p>
                </div>
              ) : pendingCount === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">Nenhum agendamento pendente</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {isAdminView
                      ? 'Todos os atendimentos do sistema estão em dia!'
                      : 'Todos os seus atendimentos estão em dia!'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {pendingAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className={`p-4 hover:bg-gray-50 transition-colors border-l-4 ${getUrgencyColor(appointment.urgencyLevel)}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getUrgencyIcon(appointment.urgencyLevel)}
                            <span className="text-xs font-medium text-gray-500">
                              {formatTimeAgo(appointment.hoursSincePassed)}
                            </span>
                          </div>

                          <h4 className="font-semibold text-gray-900 mb-1 truncate">
                            {appointment.patientName}
                          </h4>

                          <div className="space-y-1 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>
                                {clinicTimeZone
                                  ? formatInTimeZone(appointment.start, 'DD/MM/YYYY [às] HH:mm', clinicTimeZone)
                                  : 'Carregando horário da clínica...'}
                              </span>
                            </div>

                            {appointment.location && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="truncate">{appointment.location}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            onResolveAppointment(appointment);
                            setShowDropdown(false);
                          }}
                          className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Resolver
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pendingCount > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                <p className="text-xs text-gray-500">
                  Agendamentos sem confirmação há mais de 2 horas
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
